/**
 * The tick engine (v0.2 RFC §5–§7). One uniform tick(); events out; scenes
 * animate. Phaser-free. The RULED core: EVERY promotion passes the relapse
 * roll (no safe rung); graduation = completing the LAST OPEN tier; relapse
 * depth is an RNG draw with slide-down-if-full.
 */

import {
  ALUMNI,
  AUTOMATOR_ACTIONS_BY_LEVEL,
  CARE_TIERS,
  DISCHARGE,
  ECONOMY,
  METERS,
  OFFICE,
  RELAPSE,
  TICKS,
  TIERS,
  type CareTierId,
} from "@/data/tunables";
import { applyHeatGain, applyRepLoss, checkGameOver, meterUpkeep, type GameOverReason } from "@/systems/meters";
import { forcedPrestige } from "@/systems/prestige";
import { criminalDeaPerTick, criminalIncomePerTick, criminalRelapseAdd } from "@/systems/criminal";
import {
  alumniOpen,
  effectiveCapacity,
  frontierTier,
  patientInSlot,
  patientsInTier,
  progressPerTick,
  type GameState,
  type Patient,
} from "@/state/GameState";
import { scanArrivals, type MailEvent } from "@/systems/mailbox";
import { draw, drawInt } from "@/systems/rng";
import { scenePaused, stepEvents, type EventTickEvent } from "@/systems/events";
import { admitFromQueue, ratchetHighWater, recruit, tendSlot, treatSlot, type ActionEvent } from "@/systems/actions";

export type TickEvent =
  | ActionEvent
  | MailEvent
  | { type: "promoted"; patientId: number; name: string; from: CareTierId; to: CareTierId; payout: number }
  | { type: "graduated"; patientId: number; name: string; tier: CareTierId; payout: number; intoAlumni: boolean }
  | { type: "relapsed"; patientId: number; name: string; from: CareTierId; to: CareTierId; severity: "minor" | "major" }
  | { type: "died"; patientId: number; name: string; tier: CareTierId; bed: number }
  | { type: "alumniReferral"; leadId: number; name: string }
  | { type: "alumniRelapsed"; name: string }
  | { type: "gameOver"; reason: GameOverReason; payout: number }
  | EventTickEvent
  | { type: "lootCollected"; id: number; kind: "money" | "contraband"; tier: CareTierId; item: string; funding: number; heat: number; sk: number };

export interface TickResult {
  events: TickEvent[];
}

function logIncome(state: GameState, amount: number): void {
  const { run } = state;
  run.incomeLog.push({ tick: run.tick, amount });
  const cutoff = run.tick - ECONOMY.netWindowTicks;
  while (run.incomeLog.length > 0 && run.incomeLog[0]!.tick <= cutoff) {
    run.incomeLog.shift();
  }
}

export function netPerSec(state: GameState): number {
  const { run } = state;
  const cutoff = run.tick - ECONOMY.netWindowTicks;
  const total = run.incomeLog.reduce((sum, e) => (e.tick > cutoff ? sum + e.amount : sum), 0);
  const windowSec = (ECONOMY.netWindowTicks * TICKS.onlineMs) / 1000;
  return total / windowSec;
}

// ── M8: the relapse formula, LIVE (transparency panel reads the breakdown) ──

export interface RelapseBreakdown {
  base: number;
  overcrowd: number;
  undertreatAvg: number;
  policy: number;
  fullStaff: number;
  lowHeat: number;
  /** v0.5: dirty-house tax — prior broker sales worsen your patients' odds. */
  broker: number;
  /** v0.6: irony engine — your criminal supply chain worsens your patients' odds. */
  criminal: number;
  total: number;
}

interface RelapseTerms {
  base: number;
  overcrowd: number;
  undertreatAvg: number;
  policy: number;
  fullStaff: number;
  lowHeat: number;
  broker: number;
  criminal: number;
  /** UNCLAMPED sum of everything EXCEPT the undertreat term. */
  rawSansUndertreat: number;
}

function relapseTerms(state: GameState, tier: CareTierId): RelapseTerms {
  const cfg = TIERS[tier];
  const ts = state.run.tiers[tier];
  const patients = patientsInTier(state, tier);
  const capacityStaffed = RELAPSE.manualBaseline + ts.automatorLevel * RELAPSE.automatorCoverage;
  const overcrowd = patients.length > capacityStaffed ? RELAPSE.overcrowdDelta : 0;
  const avgIssues = patients.length > 0 ? patients.reduce((s, p) => s + p.issueHistory, 0) / patients.length : 0;
  const undertreatAvg = Math.min(RELAPSE.undertreatCap, RELAPSE.undertreatPerIssue * avgIssues);
  const policy = DISCHARGE[ts.dischargePolicy].relapseDelta;
  const fullStaff = ts.automatorLevel >= Math.ceil(Math.max(1, patients.length) / 2) ? RELAPSE.fullStaffDelta : 0;
  const lowHeat = state.run.heat < RELAPSE.lowHeatThreshold ? RELAPSE.lowHeatDelta : 0;
  const broker = Math.min(METERS.brokerHistoryCap, METERS.brokerHistoryPerSale * state.run.brokerHistory);
  const criminal = criminalRelapseAdd(state); // v0.6 irony engine (already capped)
  return {
    base: cfg.baseRelapse,
    overcrowd,
    undertreatAvg,
    policy,
    fullStaff,
    lowHeat,
    broker,
    criminal,
    rawSansUndertreat: cfg.baseRelapse + overcrowd + policy + fullStaff + lowHeat + broker + criminal,
  };
}

function clampPct(raw: number): number {
  return Math.min(RELAPSE.clampMax, Math.max(RELAPSE.clampMin, raw));
}

/** Tier-level breakdown for the transparency panel (avg undertreat). */
export function relapseBreakdown(state: GameState, tier: CareTierId): RelapseBreakdown {
  const t = relapseTerms(state, tier);
  return {
    base: t.base,
    overcrowd: t.overcrowd,
    undertreatAvg: t.undertreatAvg,
    policy: t.policy,
    fullStaff: t.fullStaff,
    lowHeat: t.lowHeat,
    broker: t.broker,
    criminal: t.criminal,
    total: clampPct(t.rawSansUndertreat + t.undertreatAvg),
  };
}

/** The % actually rolled for one patient — RAW terms + own history, ONE clamp
 *  (rebuilding from a clamped total distorted the extremes — v0.2 leg-1 M-1). */
function relapsePercentFor(state: GameState, p: Patient): number {
  const t = relapseTerms(state, p.tier);
  const own = Math.min(RELAPSE.undertreatCap, RELAPSE.undertreatPerIssue * p.issueHistory);
  return clampPct(t.rawSansUndertreat + own);
}

// ── travel ──────────────────────────────────────────────────────────────────

function freeSlot(state: GameState, tier: CareTierId): number {
  for (let b = 0; b < effectiveCapacity(state, tier); b++) {
    if (!patientInSlot(state, tier, b)) return b;
  }
  return -1;
}

function removePatient(state: GameState, id: number): void {
  state.run.patients = state.run.patients.filter((p) => p.id !== id);
}

/**
 * Relapse landing (RULED): minor = depth 1; major = RNG depth 2..all-the-way;
 * slide DOWN from the target until a free slot; if nothing below is free,
 * restart in place (the patient keeps their current bed).
 */
function relapseLanding(state: GameState, p: Patient, severity: "minor" | "major"): CareTierId {
  const i = CARE_TIERS.indexOf(p.tier);
  let depth: number;
  if (severity === "minor") {
    depth = 1;
  } else if (i <= 1) {
    depth = i; // residential→detox; detox→in place
  } else {
    depth = 2 + drawInt(state.run.rng, i - 1); // uniform 2..i (v0.8 owns weighting)
  }
  const target = Math.max(0, i - depth);
  // slide down from target toward detox looking for space
  for (let t = target; t >= 0; t--) {
    if (freeSlot(state, CARE_TIERS[t]!) !== -1) return CARE_TIERS[t]!;
  }
  return p.tier; // nothing below free — restart in place (keeps own bed)
}

function applyRelapse(
  state: GameState,
  p: Patient,
  severity: "minor" | "major",
  events: TickEvent[],
  movedThisTick: Set<number>,
): void {
  const from = p.tier;
  const to = relapseLanding(state, p, severity);
  if (to !== p.tier) {
    p.bed = freeSlot(state, to); // relapseLanding guaranteed space
    p.tier = to;
  }
  p.state = "new";
  p.progress = 0;
  p.relapseCount += 1;
  movedThisTick.add(p.id); // destination tier must not process them this tick
  events.push({ type: "relapsed", patientId: p.id, name: p.name, from, to, severity });
}

/**
 * Completion attempt — the RULED gate: EVERY rung rolls. Promotions
 * additionally require a free slot in the next tier; when the destination is
 * full the patient HOLDS at 100 (no roll until the move can actually happen —
 * the blocked-promotion rule, recorded in COMPLIANCE).
 */
function attemptCompletion(
  state: GameState,
  p: Patient,
  events: TickEvent[],
  movedThisTick: Set<number>,
): void {
  const frontier = frontierTier(state);
  const isFrontier = frontier !== null && p.tier === frontier;
  const nextIdx = CARE_TIERS.indexOf(p.tier) + 1;
  const next = isFrontier ? null : CARE_TIERS[nextIdx]!;
  let destSlot = -1;
  if (next !== null) {
    if (!state.run.availableTiers.includes(next)) return; // guard; hold
    destSlot = freeSlot(state, next);
    if (destSlot === -1) return; // destination full — hold at 100, no roll
  }

  const pct = relapsePercentFor(state, p);
  const r = draw(state.run.rng) * 100;
  if (r < pct) {
    const minor = draw(state.run.rng) < RELAPSE.minorShare;
    if (!minor && draw(state.run.rng) < RELAPSE.deathChanceOnMajor) {
      // Rep loss routes through PR-firm mitigation + Heat compounding (v0.5);
      // the death spike raises Heat; the death joins the batched Death-Letter
      // debt (deathsPending) the mailbox surfaces.
      applyRepLoss(state, Math.abs(ECONOMY.deathReputationHit)); // magnitude, sign-agnostic (leg-4: Gemini)
      applyHeatGain(state, ECONOMY.deathHeatSpike);
      state.run.deathsEver += 1;
      state.run.deathsPending += 1;
      events.push({ type: "died", patientId: p.id, name: p.name, tier: p.tier, bed: p.bed });
      removePatient(state, p.id);
      return;
    }
    applyRelapse(state, p, minor ? "minor" : "major", events, movedThisTick);
    return;
  }

  // r >= pct → the patient COMPLETES (promotes or graduates). Aggressive
  // discharge runs the floor hot — a per-completion Heat cost (v0.5, the
  // short-stay-churn tell).
  if (state.run.tiers[p.tier].dischargePolicy === "aggressive") {
    applyHeatGain(state, METERS.aggressiveDischargeHeat);
  }

  if (next === null) {
    // GRADUATION — completing the last OPEN tier (ruled)
    const payout = Math.round(TIERS[p.tier].promotionPayout * ECONOMY.graduationMultiplier);
    state.run.funding += payout;
    state.run.reputation += ECONOMY.graduationReputation;
    state.account.lifetimeGraduations += 1;
    state.run.graduationsByTier[p.tier] += 1;
    logIncome(state, payout);
    const intoAlumni = alumniOpen(state) && p.tier === "soberLiving";
    if (intoAlumni) {
      state.run.alumni.roster.push({
        id: p.id,
        name: p.name,
        relapseCount: p.relapseCount,
        issueHistory: p.issueHistory,
      });
      ratchetHighWater(state, "alumni");
    } else {
      state.run.graduatedOut += 1; // exits the world — "outcome data unavailable"
    }
    events.push({ type: "graduated", patientId: p.id, name: p.name, tier: p.tier, payout, intoAlumni });
    removePatient(state, p.id);
    return;
  }

  // PROMOTION (survived the roll)
  const payout = TIERS[p.tier].promotionPayout;
  state.run.funding += payout;
  state.run.graduationsByTier[p.tier] += 1; // completions count per tier
  logIncome(state, payout);
  const from = p.tier;
  p.tier = next;
  p.bed = destSlot;
  p.state = "new";
  p.progress = 0;
  ratchetHighWater(state, next);
  events.push({ type: "promoted", patientId: p.id, name: p.name, from, to: next, payout });
}

// ── per-tier automator + progression step ──────────────────────────────────

function tierStep(
  state: GameState,
  tier: CareTierId,
  events: TickEvent[],
  movedThisTick: Set<number>,
): void {
  const ts = state.run.tiers[tier];
  const eligible = () => patientsInTier(state, tier).filter((p) => !movedThisTick.has(p.id));
  let actions = AUTOMATOR_ACTIONS_BY_LEVEL[ts.automatorLevel] ?? 0;
  if (actions > 0) {
    for (const p of eligible()) {
      if (actions <= 0) break;
      if (p.state === "issue") {
        treatSlot(state, tier, p.bed);
        actions -= 1;
      }
    }
    for (const p of eligible()) {
      if (actions <= 0) break;
      if (p.state === "new" || p.state === "paused") {
        tendSlot(state, tier, p.bed);
        actions -= 1;
      }
    }
    for (const p of eligible()) {
      if (actions <= 0) break;
      if (p.state === "progressing" && p.progress < 100) {
        tendSlot(state, tier, p.bed);
        actions -= 1;
      }
    }
  }

  const rate = progressPerTick(state, tier);
  for (const p of [...eligible()]) {
    if (p.state !== "progressing") continue;
    if (p.progress >= 100) {
      attemptCompletion(state, p, events, movedThisTick);
      continue;
    }
    if (draw(state.run.rng) < TIERS[tier].issueChancePerTick) {
      p.state = "issue";
      p.issueHistory += 1;
      state.run.issuesEver += 1;
      continue;
    }
    p.progress = Math.min(100, p.progress + rate);
    if (p.progress >= 100) {
      attemptCompletion(state, p, events, movedThisTick);
    }
  }
}

// ── alumni step (M11) ───────────────────────────────────────────────────────

function alumniStep(state: GameState, events: TickEvent[]): void {
  const a = state.run.alumni;
  const n = a.roster.length;
  if (n === 0) return;
  a.repFraction += n * ALUMNI.repPerAlumnusPerTick;
  if (a.repFraction >= 1) {
    const whole = Math.floor(a.repFraction);
    state.run.reputation += whole;
    a.repFraction -= whole;
  }
  const boost = state.run.tick <= a.contactBoostUntil ? ALUMNI.contactCallMultiplier : 1;
  const coord = 1 + a.coordinatorLevel * ALUMNI.coordinatorMultiplierPerLevel;
  const p = Math.min(ALUMNI.referralChanceCapPerTick, n * ALUMNI.referralChancePerAlumnusPerTick * boost * coord);
  if (draw(state.run.rng) < p && state.run.queue.length < OFFICE.queueCap) {
    const ev = recruit(state);
    if (ev.type === "leadJoined") {
      events.push(ev); // office scenes render the queue join
      events.push({ type: "alumniReferral", leadId: ev.leadId, name: ev.name });
    }
  }
  if (draw(state.run.rng) < n * ALUMNI.relapseChancePerAlumnusPerTick && state.run.queue.length < OFFICE.queueCap) {
    const idx = drawInt(state.run.rng, n);
    const alum = a.roster.splice(idx, 1)[0]!;
    // The M8 memory travels with them (ruled): history + this relapse.
    state.run.queue.push({
      id: alum.id,
      name: alum.name,
      issueHistory: alum.issueHistory,
      relapseCount: alum.relapseCount + 1,
    });
    // paired events: office scenes render the join; alumni scene the loss
    events.push({ type: "leadJoined", leadId: alum.id, name: alum.name });
    events.push({ type: "alumniRelapsed", name: alum.name });
  }
}

// ── the tick ────────────────────────────────────────────────────────────────

export function tick(state: GameState, dtTicks: number, opts?: { liveEvents?: boolean }): TickResult {
  if (!Number.isInteger(dtTicks) || dtTicks < 1) {
    throw new Error(`tick: dtTicks must be an integer >= 1, got ${dtTicks}`);
  }
  // v0.13: the event scheduler is an ONLINE-ONLY mechanism (RFC §3.5) —
  // offline catch-up passes liveEvents:false and the channels freeze.
  const liveEvents = opts?.liveEvents !== false;
  const events: TickEvent[] = [];
  const { run } = state;
  for (let i = 0; i < dtTicks; i++) {
    run.tick += 1;
    if (liveEvents) events.push(...stepEvents(state));

    const rLevel = run.office.recruiterLevel;
    const interval = OFFICE.recruiterIntervalByLevel[rLevel] ?? Infinity;
    if (Number.isFinite(interval) && run.tick % interval === 0 && !(liveEvents && scenePaused(state, "office"))) {
      events.push(recruit(state));
    }

    // continuum order: DEEPEST open tier first (promotions go up = already-
    // processed tiers). Relapses go DOWN into not-yet-processed tiers, so
    // movedThisTick guards them from same-tick reprocessing (confirmed by
    // two independent review legs).
    const movedThisTick = new Set<number>();
    for (const tier of [...CARE_TIERS].reverse()) {
      // v0.14: pause is ONLINE-ONLY — during a frozen (offline/hidden-tab)
      // batch cards can't age or be answered, so they must NOT freeze the
      // tier either, or the gap starves it (v0.14 review, swarm major).
      if (run.availableTiers.includes(tier) && !(liveEvents && scenePaused(state, tier))) {
        tierStep(state, tier, events, movedThisTick);
      }
    }

    if (!(liveEvents && scenePaused(state, "alumni"))) alumniStep(state, events);

    // mailbox arrival scan (v0.3) — cadence-gated inside
    events.push(...scanArrivals(state));

    // criminal track (v0.6): passive income feeds NET/SEC (logged); DEA accrues
    // (no natural decay — mitigation only), floored at 0. Runs BEFORE the
    // game-over check so a raid triggers on this tick's DEA.
    if (run.criminal.unlocked) {
      const inc = criminalIncomePerTick(state);
      if (inc > 0) {
        run.funding += inc;
        logIncome(state, inc);
      }
      run.dea = Math.max(0, run.dea + criminalDeaPerTick(state));
    }

    // peak net/sec — a prestige-payout input (v0.4); tracked live, never resets
    // mid-run (newRun starts it at 0).
    run.peakNetPerSec = Math.max(run.peakNetPerSec, netPerSec(state));

    // Game-over check + meter maintenance (v0.5). The threshold is a HARD gate,
    // so check it FIRST — before decay/recovery can pull a run back under an
    // already-crossed ceiling/floor (leg-4: Codex + CodeRabbit). A
    // shutdown/collapse ends the run via FORCED prestige (reduced payout,
    // high-water kept, never a wipe); catch-up stops here — the fresh run
    // resumes on the next online tick.
    const over = checkGameOver(state);
    if (over) {
      const forced = forcedPrestige(state, over);
      events.push({ type: "gameOver", reason: over, payout: forced.payout });
      break;
    }
    // meterUpkeep runs AFTER the hard-gate check (above) by design — mitigation
    // must not rescue an already-crossed ceiling. The fixer's bribe is a
    // recurring EXPENSE, so log it negative to keep NET/SEC honest (bot leg).
    const fixerBribe = meterUpkeep(state);
    if (fixerBribe > 0) logIncome(state, -fixerBribe);
  }
  return { events };
}

export function offlineTicks(offlineMs: number): number {
  if (!Number.isFinite(offlineMs)) {
    throw new Error(`offlineTicks: offlineMs must be a finite number, got ${offlineMs}`);
  }
  return Math.floor(Math.min(Math.max(0, offlineMs), TICKS.offlineCapMs) / TICKS.offlineMs);
}

export { admitFromQueue, recruit };
